import { motion } from "framer-motion";
import { useEffect, useMemo, useState } from "react";
import { Lightbox } from "@/components/storefront/Lightbox";
import { PageHeader } from "@/components/storefront/PageHeader";
import { Seo } from "@/components/Seo";
import { Skeleton } from "@/components/ui/skeleton";
import { API, imgUrl } from "@/lib/api";

export default function Gallery() {
  const [cats, setCats] = useState([]);
  const [images, setImages] = useState(null);
  const [active, setActive] = useState("");
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    API.get("/gallery/categories").then((r) => setCats(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setImages(null);
    API.get("/gallery", { params: active ? { category: active } : {} })
      .then((r) => setImages(r.data))
      .catch(() => setImages([]));
  }, [active]);

  const activeName = useMemo(() => cats.find((c) => c.id === active)?.name || "All", [cats, active]);

  return (
    <div data-testid="gallery-page">
      <Seo title="Gallery | Eileen Jenkins Florist" description="A selection of our recent bouquets, arrangements, wedding flowers and tributes." />
      <PageHeader title="Gallery" subtitle="A selection of our recent work — every piece made by hand in the shop." testId="gallery-header" />

      <div className="max-w-7xl mx-auto px-4 pb-20">
        <div className="flex gap-2 overflow-x-auto pb-4 mb-8 -mx-4 px-4" data-testid="gallery-filters">
          <button
            onClick={() => setActive("")}
            data-testid="gallery-filter-all"
            className={`shrink-0 px-4 py-2 rounded-full text-sm border transition-colors ${!active ? "bg-primary text-primary-foreground border-primary" : "hover:border-primary"}`}
          >
            All
          </button>
          {cats.map((c) => (
            <button
              key={c.id}
              onClick={() => setActive(c.id)}
              data-testid={`gallery-filter-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
              className={`shrink-0 px-4 py-2 rounded-full text-sm border transition-colors ${active === c.id ? "bg-primary text-primary-foreground border-primary" : "hover:border-primary"}`}
            >
              {c.name}
            </button>
          ))}
        </div>

        {images === null ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => <Skeleton key={i} className="aspect-square rounded-xl" />)}
          </div>
        ) : images.length === 0 ? (
          <p className="text-center text-muted-foreground py-16" data-testid="gallery-empty">No images in {activeName} yet.</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="gallery-grid">
            {images.map((img, i) => (
              <motion.button
                key={img.id}
                initial={{ opacity: 0, scale: 0.96 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: Math.min(i * 0.04, 0.4) }}
                onClick={() => setLightbox(i)}
                data-testid={`gallery-image-${img.id}`}
                className="group relative overflow-hidden rounded-xl aspect-square"
              >
                <img src={imgUrl(img.url)} alt={img.caption || "Gallery image"} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                {img.caption && (
                  <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent text-white text-xs p-3 pt-8 text-left opacity-0 group-hover:opacity-100 transition-opacity">
                    {img.caption}
                  </span>
                )}
              </motion.button>
            ))}
          </div>
        )}
      </div>

      <Lightbox images={images || []} index={lightbox} onClose={() => setLightbox(null)} onNavigate={setLightbox} />
    </div>
  );
}
